import { useQuery } from "@tanstack/react-query";
import { Button, Drawer, Flex, Modal, Spin, Typography } from "antd";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCards } from "../api";
import { Card } from "../types";
import { CardAttachButton } from "./card-attach-button";
import styles from "./index.module.scss";

export type CardAttachModalsType = {
  open: () => void;
  close: () => void;
  openAlertModal: () => void;
  closeAlertModal: () => void;
};

type CardAttachModalsProps = {
  getPayout: ({ id, cardId }: { id: string; cardId?: string }) => void;
  isPending: boolean;
  id: string;
};

export const CardAttachModals = forwardRef<
  CardAttachModalsType,
  CardAttachModalsProps
>(({ id, isPending, getPayout }, ref) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [isCheckCardModalVisible, setIsCheckCardModalVisible] = useState(false);
  const [isCardPayoutsAlertModalVisible, setIsCardPayoutsAlertModalVisible] =
    useState(false);

  const { data: cards, isLoading } = useQuery({
    queryKey: ["cards"],
    queryFn: () => getCards(),
  });

  useImperativeHandle(ref, () => ({
    open: () => setIsModalVisible(true),
    close: () => setIsModalVisible(false),
    openAlertModal: () => setIsCardPayoutsAlertModalVisible(true),
    closeAlertModal: () => setIsCardPayoutsAlertModalVisible(false),
  }));

  return (
    <>
      <Drawer
        title={t("payment_details.select_card")}
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        footer={null}
        placement="bottom"
        destroyOnHidden
      >
        {isLoading || isPending ? (
          <div className={styles.modalWrapperLoader}>
            <Spin />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100%",
            }}
          >
            <div style={{ marginBottom: 16 }}>
              {cards?.data.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => {
                    setSelectedCard(item);
                    setIsCheckCardModalVisible(true);
                  }}
                  type="link"
                  style={{ marginBottom: 16, padding: 0 }}
                  block
                >
                  {item.pan_masked}
                </Button>
              ))}
            </div>

            <CardAttachButton />
          </div>
        )}
      </Drawer>

      <Modal
        title={
          <Typography.Title level={3}>
            {t("payment_details.payout_alert_title")}
          </Typography.Title>
        }
        open={isCardPayoutsAlertModalVisible}
        onCancel={() => {
          setIsCardPayoutsAlertModalVisible(false);
          localStorage.setItem("showCardPayoutsAlert", "show");
          setIsModalVisible(true);
        }}
        footer={
          <Button
            type="primary"
            onClick={() => {
              setIsCardPayoutsAlertModalVisible(false);
              localStorage.setItem("showCardPayoutsAlert", "show");
              setIsModalVisible(true);
            }}
          >
            {t("payment_details.got_it")}
          </Button>
        }
        destroyOnHidden
      >
        <Typography.Text style={{ display: "block", marginBottom: 12 }}>
          {t("payment_details.payout_alert_intro")}
        </Typography.Text>
        <Typography.Text style={{ display: "block", marginBottom: 4 }}>
          {t("payment_details.payout_alert_step1")}
        </Typography.Text>
        <Typography.Text style={{ display: "block", marginBottom: 4 }}>
          {t("payment_details.payout_alert_step2")}
        </Typography.Text>
        <Typography.Text style={{ display: "block", marginBottom: 16 }}>
          {t("payment_details.payout_alert_step3")}
        </Typography.Text>
        <Typography.Text style={{ display: "block" }}>
          {t("payment_details.payout_alert_note")}
        </Typography.Text>
      </Modal>

      <Modal
        title={
          <Typography.Title level={4}>
            {t("payment_details.confirm_card_title")}
          </Typography.Title>
        }
        open={isCheckCardModalVisible}
        onCancel={() => {
          setIsCheckCardModalVisible(false);
        }}
        footer={
          <Flex gap="small" justify="end">
            <Button
              onClick={() => {
                setIsCheckCardModalVisible(false);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                getPayout({ id: id, cardId: selectedCard?.id });
                setIsCheckCardModalVisible(false);
                setIsModalVisible(false);
              }}
            >
              {t("common.confirm")}
            </Button>
          </Flex>
        }
        destroyOnHidden
      >
        <Typography.Text style={{ display: "block", marginBottom: 8 }}>
          {t("payment_details.confirm_card_description")}{" "}
          <Typography.Text
            style={{
              color: "var(--ant-color-primary)",
            }}
          >
            {selectedCard?.pan_masked}
          </Typography.Text>
        </Typography.Text>
      </Modal>
    </>
  );
});
